import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';
import { getTextStyleClasses, type TextStyle } from '@/lib/typography';

type TextTagName = keyof HTMLElementTagNameMap;

export type TextProps = {
  /** Typography scale token, e.g. `heading.xl` or `body.m`. */
  textStyle?: TextStyle;
  /** HTML element to render. Defaults to `div`. */
  tagName?: TextTagName;
  /** Apply semantic text colour from the scale (default true). */
  colour?: boolean;
  asChild?: boolean;
  className?: string;
  children?: React.ReactNode;
  id?: string;
  htmlFor?: string;
  dateTime?: string;
  scope?: 'row' | 'col' | 'rowgroup' | 'colgroup';
} & Omit<React.HTMLAttributes<HTMLElement>, 'color'>;

const Text = React.forwardRef<HTMLElement, TextProps>(
  (
    {
      textStyle = 'body.m',
      tagName = 'div',
      colour = true,
      asChild = false,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = (asChild ? Slot : tagName) as React.ElementType;

    return (
      <Comp
        ref={ref as React.Ref<never>}
        className={cn(getTextStyleClasses(textStyle, colour), className)}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Text.displayName = 'Text';

export interface TextWrapperRenderProps {
  textStyleClasses: string;
}

export interface TextWrapperProps {
  textStyle?: TextStyle;
  colour?: boolean;
  className?: string;
  children: (slotProps: TextWrapperRenderProps) => React.ReactNode;
}

/** Apply typography classes to arbitrary child markup via render props. */
function TextWrapper({
  textStyle = 'body.m',
  colour = true,
  className,
  children,
}: TextWrapperProps) {
  const textStyleClasses = cn(getTextStyleClasses(textStyle, colour), className);
  return <>{children({ textStyleClasses })}</>;
}

export { Text, TextWrapper, getTextStyleClasses };
